import { PubSubService } from '@ohif/core';
import {
  AssessmentSubmission,
  FindingAssessment,
  fetchReportAssessments,
  submitReportAssessments,
} from '../clients/viewer';

export interface PendingAssessment extends FindingAssessment {
  isSubmitted: false;
}

export interface SubmittedAssessment extends FindingAssessment {
  isSubmitted: true;
  isDetectionCorrect: boolean;
}

export type TrackedAssessment = PendingAssessment | SubmittedAssessment;

type SubmittableAssessment = PendingAssessment & { isDetectionCorrect: boolean };

export interface ReportAssessmentStudyState {
  assessments: readonly TrackedAssessment[];
  assessmentRequired: boolean;
  isReportGenerated: boolean;
  reportId?: string;
}

const UNTRACKED: ReportAssessmentStudyState = Object.freeze({
  assessments: Object.freeze([]),
  assessmentRequired: false,
  isReportGenerated: false,
});

const EVENTS = {
  ASSESSMENT_UPDATED: 'event::sycai:ReportAssessmentService:assessmentUpdated',
  ASSESSMENT_SUBMITTED: 'event::sycai:ReportAssessmentService:assessmentSubmitted',
} as const;

export type ReportAssessmentEventPayloads = {
  [EVENTS.ASSESSMENT_UPDATED]: { studyInstanceUid: string };
  [EVENTS.ASSESSMENT_SUBMITTED]: { studyInstanceUid: string };
};

export class ReportAssessmentService extends PubSubService {
  public static readonly EVENTS = EVENTS;

  public static readonly REGISTRATION = {
    name: 'reportAssessmentService',
    create: () => new ReportAssessmentService(),
  };

  private emit<E extends keyof ReportAssessmentEventPayloads>(
    event: E,
    payload: ReportAssessmentEventPayloads[E]
  ): void {
    this._broadcastEvent(event, payload);
  }

  private studies = new Map<string, ReportAssessmentStudyState>();

  constructor() {
    super(EVENTS);
  }

  public getStudy(studyInstanceUid: string): ReportAssessmentStudyState {
    return this.studies.get(studyInstanceUid) ?? UNTRACKED;
  }

  /**
   * Eviction belongs to the mode lifecycle, not to a component's cleanup.
   *
   * This drops the local submission record too, so against a server that never echoes a
   * submission the protection lasts one mode entry: leave such a study and come back and
   * it asks to be assessed again. Deliberately not carried further — what the client may
   * remember that the backend does not is a product question, not a caching one.
   */
  public onModeExit(): void {
    this.studies.clear();
  }

  public isNoFindingsMode(studyInstanceUid: string): boolean {
    return isNoFindings(this.getStudy(studyInstanceUid).assessments);
  }

  public async refreshStudy(studyInstanceUid: string): Promise<ReportAssessmentStudyState> {
    const { reportId, findingAssessments } = await fetchReportAssessments(studyInstanceUid);

    const incoming = fromServer(findingAssessments);
    const previous = this.studies.get(studyInstanceUid);

    const isSameReport =
      previous !== undefined &&
      previous.reportId === reportId &&
      previous.assessments.length === incoming.length;

    const assessments = isSameReport ? mergeAssessments(previous.assessments, incoming) : incoming;

    const next: ReportAssessmentStudyState = {
      assessments,
      assessmentRequired: isAssessmentRequired(assessments),
      isReportGenerated: Boolean(reportId),
      reportId,
    };

    this.studies.set(studyInstanceUid, next);
    return next;
  }

  public setVerdict(
    studyInstanceUid: string,
    index: number,
    field: 'isClassificationCorrect' | 'isDetectionCorrect',
    value: boolean | undefined
  ): void {
    const current = this.getStudy(studyInstanceUid);

    const assessments = current.assessments.map((assessment, row) =>
      row === index && !assessment.isSubmitted
        ? ({ ...assessment, [field]: value } as PendingAssessment)
        : assessment
    );

    this.studies.set(studyInstanceUid, { ...current, assessments });
    this.emit(EVENTS.ASSESSMENT_UPDATED, { studyInstanceUid });
  }

  private submittableRows(
    studyInstanceUid: string
  ): Array<{ index: number; row: SubmittableAssessment }> {
    return submittableRowsOf(this.getStudy(studyInstanceUid).assessments);
  }

  public getSubmittable(studyInstanceUid: string): AssessmentSubmission[] {
    const noFindings = this.isNoFindingsMode(studyInstanceUid);

    return this.submittableRows(studyInstanceUid).map(({ row }) =>
      noFindings
        ? { findingId: null, isDetectionCorrect: row.isDetectionCorrect }
        : {
            findingId: row.findingId ?? null,
            name: row.name,
            isClassificationCorrect: row.isClassificationCorrect,
            isDetectionCorrect: row.isDetectionCorrect,
          }
    );
  }

  public async submitStudy(studyInstanceUid: string): Promise<void> {
    const rows = this.submittableRows(studyInstanceUid);
    const submission = this.getSubmittable(studyInstanceUid);

    if (submission.length === 0) {
      return;
    }

    await submitReportAssessments(studyInstanceUid, submission);

    this.recordSubmission(studyInstanceUid, rows);

    await this.refreshStudy(studyInstanceUid);

    this.emit(EVENTS.ASSESSMENT_SUBMITTED, { studyInstanceUid });
  }

  private recordSubmission(
    studyInstanceUid: string,
    submitted: Array<{ index: number; row: SubmittableAssessment }>
  ): void {
    const current = this.getStudy(studyInstanceUid);
    const sent = new Map(submitted.map(({ index, row }) => [index, row]));

    const assessments = current.assessments.map((row, index): TrackedAssessment => {
      const captured = sent.get(index);
      return captured ? { ...captured, isSubmitted: true } : row;
    });

    this.studies.set(studyInstanceUid, {
      ...current,
      assessments,
      assessmentRequired: isAssessmentRequired(assessments),
    });
  }
}

export interface ReportAssessmentService {
  subscribe: (<E extends keyof ReportAssessmentEventPayloads>(
    eventName: E,
    callback: (data: ReportAssessmentEventPayloads[E]) => void
  ) => { unsubscribe: () => void }) &
    PubSubService['subscribe'];
}

function fromServer(findingAssessments: FindingAssessment[]): TrackedAssessment[] {
  if (findingAssessments.length === 0) {
    return [{ findingId: null, isSubmitted: false }];
  }

  const noFindings = isNoFindings(findingAssessments);

  return findingAssessments.map(row => toTracked(row, noFindings));
}

function toTracked(row: FindingAssessment, noFindings: boolean): TrackedAssessment {
  const { isClassificationCorrect, isDetectionCorrect } = row;

  if (isDetectionCorrect == null) {
    return { ...row, isSubmitted: false };
  }

  if (!noFindings && isClassificationCorrect == null) {
    return { ...row, isSubmitted: false };
  }

  return { ...row, isSubmitted: true, isDetectionCorrect };
}

/**
 * Pure, so the service and the dialog answer "what would a submit send?" from the same
 * code — the service by study UID, the dialog from the rows it is already subscribed to.
 */
function submittableRowsOf(
  assessments: readonly TrackedAssessment[]
): Array<{ index: number; row: SubmittableAssessment }> {
  const noFindings = isNoFindings(assessments);

  return assessments
    .map((row, index) => ({ index, row }))
    .filter((entry): entry is { index: number; row: SubmittableAssessment } => {
      const { row } = entry;

      if (row.isSubmitted || row.isDetectionCorrect == null) {
        return false;
      }

      // A no-findings study answers one question, so detection alone completes it.
      return noFindings || row.isClassificationCorrect != null;
    });
}

export function hasSubmittableRows(rows: readonly TrackedAssessment[]): boolean {
  return submittableRowsOf(rows).length > 0;
}

export function isNoFindings(rows: readonly { findingId?: string | null }[]): boolean {
  return rows.length === 1 && !rows[0].findingId;
}

function isAssessmentRequired(assessments: readonly TrackedAssessment[]): boolean {
  return assessments.some(assessment => !assessment.isSubmitted);
}

function mergeAssessments(
  previous: readonly TrackedAssessment[],
  incoming: readonly TrackedAssessment[]
): TrackedAssessment[] {
  return incoming.map((row, index) => {
    const local = previous[index];

    return local?.isSubmitted ? withEcho(local, row) : row;
  });
}

function withEcho(local: SubmittedAssessment, echo: TrackedAssessment): SubmittedAssessment {
  const merged: SubmittedAssessment = { ...local };

  if (echo.name != null) {
    merged.name = echo.name;
  }

  if (echo.isClassificationCorrect != null) {
    merged.isClassificationCorrect = echo.isClassificationCorrect;
  }

  if (echo.isDetectionCorrect != null) {
    merged.isDetectionCorrect = echo.isDetectionCorrect;
  }

  return merged;
}
